(() => {
  'use strict';

  const ITEM_WIDTH_DESKTOP = 320;
  const ITEM_WIDTH_MOBILE = 280;
  const GAP = 16;
  const DESKTOP_MQ = '(min-width: 990px)';

  const SELECTORS = {
    trackWrapper: '.pdp-six-conditions__track-wrapper',
    track: '.pdp-six-conditions__track',
    item: '.pdp-six-conditions__item',
    toggle: '.pdp-six-conditions__item-toggle',
    prevBtn: '.pdp-six-conditions__arrow--prev',
    nextBtn: '.pdp-six-conditions__arrow--next',
  };

  const CLASS_OPEN = 'pdp-six-conditions__item--open';

  class PdpSixConditions extends HTMLElement {
    connectedCallback() {
      this._trackWrapper = this.querySelector(SELECTORS.trackWrapper);
      this._track = this.querySelector(SELECTORS.track);
      this._items = [...this.querySelectorAll(SELECTORS.item)];
      this._prevBtn = this.querySelector(SELECTORS.prevBtn);
      this._nextBtn = this.querySelector(SELECTORS.nextBtn);

      this._currentIndex = 0;
      this._mq = window.matchMedia(DESKTOP_MQ);

      this._boundPrev = () => this._slide(-1);
      this._boundNext = () => this._slide(1);
      this._boundMqChange = () => this._onMqChange();

      this._prevBtn?.addEventListener('click', this._boundPrev);
      this._nextBtn?.addEventListener('click', this._boundNext);
      this._mq.addEventListener('change', this._boundMqChange);

      this._items.forEach((item) => {
        const toggleBtn = item.querySelector(SELECTORS.toggle);
        toggleBtn?.addEventListener('click', () => this._toggleItem(item));
      });

      this._updateArrows();

      // Use ResizeObserver instead of window.resize to avoid iOS Safari firing
      // on address-bar show/hide (which changes viewport height, not component width)
      this._lastObservedWidth = 0;
      this._resizeObserver = new ResizeObserver((entries) => {
        const newWidth = Math.round(entries[0]?.contentRect.width ?? 0);
        if (newWidth !== this._lastObservedWidth) {
          this._lastObservedWidth = newWidth;
          clearTimeout(this._resizeTimer);
          this._resizeTimer = setTimeout(() => this._equalizeCardHeights(), 150);
        }
      });
      this._resizeObserver.observe(this);

      // Defer initial measurement so the browser has laid out the cards
      requestAnimationFrame(() => this._equalizeCardHeights());
    }

    disconnectedCallback() {
      this._prevBtn?.removeEventListener('click', this._boundPrev);
      this._nextBtn?.removeEventListener('click', this._boundNext);
      this._mq?.removeEventListener('change', this._boundMqChange);
      this._resizeObserver?.disconnect();
      clearTimeout(this._resizeTimer);
    }

    get _itemWidth() {
      return this._mq.matches ? ITEM_WIDTH_DESKTOP : ITEM_WIDTH_MOBILE;
    }

    get _visibleCount() {
      if (!this._trackWrapper) return 1;
      const availableWidth = this._trackWrapper.offsetWidth;
      return Math.max(1, Math.floor((availableWidth + GAP) / (this._itemWidth + GAP)));
    }

    get _maxIndex() {
      return Math.max(0, this._items.length - this._visibleCount);
    }

    _slide(direction) {
      const newIndex = this._currentIndex + direction;
      if (newIndex < 0 || newIndex > this._maxIndex) return;
      this._currentIndex = newIndex;
      this._applyTransform();
      this._updateArrows();
    }

    _applyTransform() {
      if (!this._track) return;
      const offset = this._currentIndex * (this._itemWidth + GAP);
      this._track.style.transform = `translateX(-${offset}px)`;
    }

    _updateArrows() {
      if (this._prevBtn) {
        this._prevBtn.disabled = this._currentIndex <= 0;
      }
      if (this._nextBtn) {
        this._nextBtn.disabled = this._currentIndex >= this._maxIndex;
      }
    }

    _onMqChange() {
      this._currentIndex = 0;
      this._applyTransform();
      this._updateArrows();
      this._equalizeCardHeights();
    }

    _equalizeCardHeights() {
      // Don't measure while any card is open — expanded height would corrupt the baseline
      if (this._items.some((item) => item.classList.contains(CLASS_OPEN))) return;

      // Reset min-height to get each card's natural closed height
      this._items.forEach((item) => {
        item.style.minHeight = '';
      });

      const maxHeight = this._items.reduce((max, item) => {
        return Math.max(max, item.offsetHeight);
      }, 0);

      if (maxHeight > 0) {
        this._items.forEach((item) => {
          item.style.minHeight = `${maxHeight}px`;
        });
      }
    }

    _toggleItem(item) {
      const isOpen = item.classList.toggle(CLASS_OPEN);
      const toggleBtn = item.querySelector(SELECTORS.toggle);
      if (toggleBtn) {
        toggleBtn.setAttribute('aria-expanded', String(isOpen));
        toggleBtn.setAttribute('aria-label', isOpen ? 'Collapse' : 'Expand');
      }
    }
  }

  if (!customElements.get('pdp-six-conditions')) {
    customElements.define('pdp-six-conditions', PdpSixConditions);
  }
})();
